/* eslint-disable */
// MIT License

// Copyright (c) 2017 Salvatore Ravidà

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

// Large parts of AdalAuthenticationProvider  were adapted from react-adal.
import { KustoDomains, User } from '@kusto/common';
import AuthenticationContext from 'adal-angular';
import { runWithAdal } from 'react-adal';
import { getTelemetryClient } from '../../utils/telemetryClient';
import { decodeJwtToken } from './authenticationUtils';
import { AuthenticationProvider } from './AuthenticationProvider';

const { trackEvent } = getTelemetryClient({
    component: 'AdalAuthenticationProvider',
    flow: '',
});

const DefaultAnyMultiTenant = 'common';

export class AdalAuthenticationProvider implements AuthenticationProvider {
    private readonly config: AuthenticationContext.Options;
    private readonly authContext: AuthenticationContext;
    private readonly defaultResource: string;

    // In any case of issue accessing resource by external tenant (non-microsoft) try replacing the
    // resource uri with app id ...
    constructor(private redirectUri: string, private clientId: string, resource?: string) {
        // Enable ADAL logging
        // ADAL expects to have a global window.Logging object where it will send logs to.
        if (typeof window !== undefined) {
            window.Logging = {
                level: 2,
                piiLoggingEnabled: false,
                log: (message) => {
                    if (message && message.length !== 0) {
                        trackEvent('adal', { message });
                    }
                },
            };
        }

        this.defaultResource = resource ?? 'https://help' + KustoDomains.Default;
        this.config = {
            // clientId: 'c8dbda70-81cc-49e0-aa47-6133d7154fe3', // Kusto Web 3rd party
            // clientId: 'f9818e52-50bd-463e-8932-a1650bd3fad2', // old kustoweb 1st party
            clientId: this.clientId,
            tenant: DefaultAnyMultiTenant, // multi tenant
            popUp: false,
            redirectUri: this.redirectUri,
            cacheLocation: 'localStorage',
            // for national clouds replace 'https://login.microsoftonline.com/'
            instance: process.env.REACT_APP_AAD_ENDPOINT,
            // Allow more time to load frame (for slower proxy connections we can get token renewal timeout)
            loadFrameTimeout: 20000,
        };
        this.authContext = new AuthenticationContext(this.config);
    }

    logOut(): void {
        this.authContext.logOut();
    }

    login() {
        this.authContext.login();
    }
    loginInProgress() {
        return this.authContext.loginInProgress();
    }

    setAuthContextExtraQueryParameter(value?: string) {
        if (value) {
            this.authContext.config.extraQueryParameter = value;
        } else {
            delete this.authContext.config.extraQueryParameter;
        }
    }

    clearCache() {
        this.authContext.clearCache();
    }

    /**
     * This is taken from react-adal as is _except_  adding AADSTS16002 to the list of exceptions that require redirect.
     * also removed the callback option (since we don't use pop up).
     */
    adalGetToken(authContext: AuthenticationContext, resourceGuiId: string) {
        return new Promise<string>((resolve, reject) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            authContext.acquireToken(resourceGuiId, (message: string | null, token: string | null, msg: any) => {
                if (!msg && token) {
                    resolve(token);
                } else if (
                    // Error code might have additional digit
                    message?.includes('AADSTS50076') ||
                    message?.includes('AADSTS50079') ||
                    message?.includes('AADSTS16002') ||
                    message?.includes('AADSTS50133') // dodopizza.com error
                ) {
                    // Default to redirect for multi-factor authentication,
                    // but allow using popup if a callback is provided
                    this.authContext.acquireTokenRedirect(resourceGuiId);
                } else {
                    reject({ message, msg });
                }
            });
        });
    }

    // Adding scopes for future transition to MSAL
    async getToken(scopes?: string[], tokenExpired?: (message: string) => void): Promise<string> {
        let resourceId = '';

        if (!scopes) {
            // Request the Kusto access token if no scopes are provided
            resourceId = this.defaultResource;
        } else {
            if (!scopes.length) {
                throw new Error('No scopes provided for the access token request.');
            }

            const scope = scopes[0];
            const index = scope.lastIndexOf('/');

            if (index > -1) {
                resourceId = scope.substring(0, index);
            } else {
                resourceId = 'https://graph.microsoft.com';
            }
        }

        const token = await this.adalGetToken(this.authContext, resourceId);

        // TODO: consider doing this only when the query fails.
        // When multiple kustoweb tabs are open there can be a race condition where the token expiry adal stores
        // in its cache is wrong (i.e doesn't represent the real exp date in the token).
        // specifically - token is no longer valid (expired), but the expiry date that adal.js stores is a valid date.
        // thus adal js decides to return the token to us, but it's damaged and will never work and the service will
        // reject it.
        // Thus, we validate it here before returning to client.
        const decodedToken = decodeJwtToken(token);

        if (decodedToken && decodedToken.exp && decodedToken.exp < Math.round(new Date().getTime() / 1000.0) + 300) {
            if (tokenExpired) {
                tokenExpired(`exp time: ${decodedToken.exp}, current: ${new Date().getTime()}`);
            }
            this.authContext.clearCacheForResource(resourceId);
            return this.adalGetToken(this.authContext, resourceId);
        }

        return token;
    }

    refreshToken() {
        return false;
    }

    getCachedUser(): User {
        return this.authContext.getCachedUser();
    }

    getUser(): Promise<User> {
        return new Promise<User>((resolve, reject) => {
            this.authContext.getUser((message: string | null, user: AuthenticationContext.UserInfo | null) => {
                if (user) {
                    resolve(user);
                } else {
                    reject(message);
                }
            });
        });
    }
    async getUserName(): Promise<string> {
        const user = await this.getUser();
        return user.userName;
    }

    authenticateAndRun(action: () => void) {
        runWithAdal(this.authContext, action);
    }

    async setTenant(tenant: string) {
        const prevTenant = this.authContext.config.tenant;

        this.authContext.config.tenant = tenant;
        // Clear access tokens from cache for all resources.
        // Unfortunately adal does not offer a built-in way to clear access tokens without clearing the id token.
        // We thus access local storage directly here to get the list of resources that have cached tokens.
        const resources = Object.keys(localStorage)
            .filter((key) => key.startsWith('adal.access.token.key'))
            .map((k) => k.substring('adal.access.token.key'.length));
        resources.forEach((r) => this.authContext.clearCacheForResource(r));

        // preloading of token
        try {
            await this.adalGetToken(this.authContext, this.defaultResource);
        } catch (ex) {
            this.authContext.config.tenant = prevTenant;
            return Promise.reject(ex);
        }
    }
    async switchTenant(tenant: string) {
        const currentTenant = this.getTenant();
        if (currentTenant === tenant) {
            return;
        }
        await this.setTenant(tenant);
    }

    getTenant(): string {
        // Actual tenant is set on the authContext
        if (this.authContext.config.tenant && this.authContext.config.tenant !== DefaultAnyMultiTenant) {
            return this.authContext.config.tenant;
        }
        // in case authContext tenant is the default, take the tenant id from user data
        const user = this.authContext.getCachedUser();
        return user?.profile?.tid;
    }

    /**
     * Checks whether the account is a consumer facing account (MSA)
     */
    getIsMSAAccount(): boolean {
        const tenant = this.getTenant();

        return (
            // MSA common
            tenant === '9188040d-6c67-4c5b-b112-36a304b66dad' ||
            // First party app global tenant
            tenant === 'f8cdef31-a31e-4b4a-93e4-5f571e91255a'
        );
    }

    getLoginError() {
        const url = new URL(window.location.href);
        if (window === window.parent && this.authContext.isCallback(url.hash)) {
            const requestInfo = this.authContext.getRequestInfo(url.hash);
            const errorDesc = requestInfo.parameters.error_description;
            const error = requestInfo.parameters.error;
            if ((error && error !== '') || (errorDesc && errorDesc !== '')) {
                return error + ' ' + errorDesc;
            }
        }

        return undefined;
    }
}
